import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Fade,
  Slide,
  Zoom,
  Grow
} from '@mui/material';
import {
  EmojiEvents,
  Star,
  Close,
  Celebration,
  LocalFireDepartment,
  Diamond,
  AutoAwesome
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import confetti from 'canvas-confetti';

// Unique Animation Keyframes
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 193, 7, 0.5); }
  50% { box-shadow: 0 0 40px rgba(255, 193, 7, 0.8), 0 0 60px rgba(255, 193, 7, 0.6); }
`;

const bounceIn = keyframes`
  0% { transform: scale(0.3) rotate(-180deg); opacity: 0; }
  50% { transform: scale(1.1) rotate(-10deg); }
  70% { transform: scale(0.9) rotate(5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const floatUp = keyframes`
  0% { transform: translateY(100px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

// Styled Components
const NotificationCard = styled(Card, {
  shouldForwardProp: (prop) => !['priority', 'animationType'].includes(prop)
})(({ theme, priority, animationType }) => ({
  position: 'relative',
  overflow: 'visible',
  background: priority >= 4 
    ? 'linear-gradient(135deg, #FFD700, #FFA000, #FF6F00)'
    : priority >= 3
    ? 'linear-gradient(135deg, #9C27B0, #673AB7)'
    : priority >= 2
    ? 'linear-gradient(135deg, #2196F3, #1976D2)'
    : 'linear-gradient(135deg, #4CAF50, #388E3C)',
  color: 'white',
  animation: animationType === 'GLOW' ? `${glowPulse} 2s infinite` :
             animationType === 'BOUNCE' ? `${bounceIn} 0.8s ease-out` :
             animationType === 'SHAKE' ? `${shakeAnimation} 0.6s ease-in-out` :
             animationType === 'FLOAT' ? `${floatUp} 0.6s ease-out` : 'none',
  '&::before': priority >= 4 ? {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    background: 'linear-gradient(45deg, #FFD700, #FF6F00, #FFD700)',
    borderRadius: 'inherit',
    zIndex: -1,
    animation: `${glowPulse} 1.5s infinite`
  } : {}
}));

const ParticleEffect = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  overflow: 'hidden'
});

const AchievementNotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  
  // Enable audio context on first user interaction
  useEffect(() => {
    const enableAudio = () => {
      window.audioContextAllowed = true;
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
    
    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, []);

  useEffect(() => {
    // Listen for achievement notifications
    const handleAchievementUnlock = (event) => {
      const notification = event.detail;
      showNotification(notification);
    };

    window.addEventListener('achievementUnlocked', handleAchievementUnlock);
    return () => window.removeEventListener('achievementUnlocked', handleAchievementUnlock);
  }, []);

  const showNotification = (notification) => {
    setCurrentNotification(notification);
    
    // Trigger effects based on priority and type
    triggerEffects(notification);
    
    // Auto-hide after duration
    setTimeout(() => {
      setCurrentNotification(null);
    }, notification.displayDuration || 5000);
  };

  const triggerEffects = (notification) => {
    const { priority, animationType, soundEffect } = notification;
    
    // Visual Effects
    switch (animationType) {
      case 'CONFETTI':
        triggerConfetti(priority);
        break;
      case 'FIREWORKS':
        triggerFireworks();
        break;
      case 'SPARKLES':
        triggerSparkles();
        break;
    }
    
    // Sound Effects
    playSound(soundEffect, priority);
    
    // Screen Effects for Epic Achievements
    if (priority >= 4) {
      triggerScreenFlash();
    }
  };

  const triggerConfetti = (priority) => {
    const particleCount = priority >= 4 ? 200 : priority >= 3 ? 100 : 50;
    const colors = priority >= 4 
      ? ['#FFD700', '#FFA000', '#FF6F00']
      : priority >= 3
      ? ['#9C27B0', '#673AB7', '#E91E63']
      : ['#2196F3', '#1976D2', '#03DAC6'];
    
    confetti({
      particleCount,
      spread: 70,
      origin: { y: 0.6 },
      colors
    });
    
    if (priority >= 4) {
      // Multiple bursts for epic achievements
      setTimeout(() => confetti({ particleCount: 100, spread: 60, origin: { x: 0.2, y: 0.7 }, colors }), 200);
      setTimeout(() => confetti({ particleCount: 100, spread: 60, origin: { x: 0.8, y: 0.7 }, colors }), 400);
    }
  };

  const triggerFireworks = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      const particleCount = 50 * (timeLeft / duration);
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      }));
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      }));
    }, 250);
  };

  const triggerSparkles = () => {
    // Create floating sparkle elements
    for (let i = 0; i < 20; i++) {
      setTimeout(() => {
        const sparkle = document.createElement('div');
        sparkle.innerHTML = '✨';
        sparkle.style.position = 'fixed';
        sparkle.style.left = Math.random() * window.innerWidth + 'px';
        sparkle.style.top = Math.random() * window.innerHeight + 'px';
        sparkle.style.fontSize = '20px';
        sparkle.style.pointerEvents = 'none';
        sparkle.style.zIndex = '9999';
        sparkle.style.animation = 'fadeOut 2s ease-out forwards';
        document.body.appendChild(sparkle);
        
        setTimeout(() => sparkle.remove(), 2000);
      }, i * 100);
    }
  };

  const triggerScreenFlash = () => {
    const flash = document.createElement('div');
    flash.style.position = 'fixed';
    flash.style.top = '0';
    flash.style.left = '0';
    flash.style.width = '100vw';
    flash.style.height = '100vh';
    flash.style.background = 'rgba(255, 215, 0, 0.3)';
    flash.style.pointerEvents = 'none';
    flash.style.zIndex = '9998';
    flash.style.animation = 'fadeOut 0.5s ease-out forwards';
    document.body.appendChild(flash);
    
    setTimeout(() => flash.remove(), 500);
  };

  const playSound = (soundEffect, priority) => {
    // Skip audio if no user interaction yet
    if (!window.audioContextAllowed) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const playTone = (frequency, duration, type = 'sine') => {
        try {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = frequency;
          oscillator.type = type;
          
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + duration);
        } catch (error) {
          console.log('Audio playback failed:', error);
        }
      };

      switch (soundEffect) {
        case 'CHIME':
          playTone(800, 0.2);
          setTimeout(() => playTone(1000, 0.2), 100);
          break;
        case 'FANFARE':
          playTone(523, 0.3);
          setTimeout(() => playTone(659, 0.3), 150);
          setTimeout(() => playTone(784, 0.3), 300);
          setTimeout(() => playTone(1047, 0.5), 450);
          break;
        case 'CELEBRATION':
          for (let i = 0; i < 5; i++) {
            setTimeout(() => playTone(400 + i * 100, 0.1), i * 50);
          }
          break;
      }
    } catch (error) {
      console.log('Audio context not available:', error);
    }
  };

  const getNotificationIcon = (achievementType, priority) => {
    if (priority >= 4) return <Diamond sx={{ fontSize: 32 }} />;
    if (priority >= 3) return <AutoAwesome sx={{ fontSize: 28 }} />;
    if (priority >= 2) return <LocalFireDepartment sx={{ fontSize: 24 }} />;
    return <EmojiEvents sx={{ fontSize: 20 }} />;
  };

  const getNotificationTitle = (notification) => {
    const { priority, notificationType } = notification;
    
    if (priority >= 4) return "🎉 LEGENDARY ACHIEVEMENT! 🎉";
    if (priority >= 3) return "⭐ EPIC ACHIEVEMENT! ⭐";
    if (priority >= 2) return "🔥 RARE ACHIEVEMENT! 🔥";
    
    switch (notificationType) {
      case 'STREAK': return "🔥 Streak Achievement!";
      case 'MILESTONE': return "🎯 Milestone Reached!";
      case 'PROGRESS': return "📈 Progress Update!";
      default: return "🏆 Achievement Unlocked!";
    }
  };

  if (!currentNotification) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        maxWidth: 400,
        width: '100%'
      }}
    >
      <Zoom in={true} timeout={600}>
        <NotificationCard
          priority={currentNotification.priority}
          animationType={currentNotification.animationType}
          sx={{ p: 2, mb: 2 }}
        >
          <ParticleEffect />
          
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                width: 48,
                height: 48,
                animation: currentNotification.priority >= 4 ? `${glowPulse} 1s infinite` : 'none'
              }}
            >
              {getNotificationIcon(currentNotification.achievementType, currentNotification.priority)}
            </Avatar>
            
            <Box flex={1}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {getNotificationTitle(currentNotification)}
              </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {currentNotification.achievementName}
              </Typography>
              
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {currentNotification.message}
              </Typography>
              
              {currentNotification.priority >= 3 && (
                <Box mt={1}>
                  <Chip
                    label={`+${currentNotification.points || 0} points`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
              )}
            </Box>
            
            <IconButton
              size="small"
              onClick={() => setCurrentNotification(null)}
              sx={{ color: 'white', opacity: 0.7 }}
            >
              <Close />
            </IconButton>
          </Box>
        </NotificationCard>
      </Zoom>
    </Box>
  );
};

// Utility function to trigger notifications
export const triggerAchievementNotification = (notification) => {
  const event = new CustomEvent('achievementUnlocked', { detail: notification });
  window.dispatchEvent(event);
};

// Predefined notification templates
export const NOTIFICATION_TEMPLATES = {
  FIRST_ACHIEVEMENT: {
    notificationType: 'UNLOCK',
    title: 'Welcome to the Club!',
    animationType: 'CONFETTI',
    soundEffect: 'CHIME',
    priority: 2,
    displayDuration: 6000
  },
  RARE_UNLOCK: {
    notificationType: 'RARE_UNLOCK',
    title: 'Rare Achievement!',
    animationType: 'FIREWORKS',
    soundEffect: 'FANFARE',
    priority: 3,
    displayDuration: 8000
  },
  LEGENDARY_UNLOCK: {
    notificationType: 'LEGENDARY_UNLOCK',
    title: 'LEGENDARY ACHIEVEMENT!',
    animationType: 'FIREWORKS',
    soundEffect: 'CELEBRATION',
    priority: 4,
    displayDuration: 10000
  },
  STREAK_MILESTONE: {
    notificationType: 'STREAK',
    title: 'Streak Master!',
    animationType: 'GLOW',
    soundEffect: 'CHIME',
    priority: 2,
    displayDuration: 5000
  }
};

export default AchievementNotificationSystem;